'use client'

import { FC, useId } from 'react'
import cn from 'classnames'
import { useAppConfig, useGetTranslations } from '@/root/shared-hooks'
import { Spinner } from '@/root/ui/shared/Spinner/Spinner'
import { CreditOnlyPaymentFromCustAccIcon } from '@/root/ui/shared/icons/CreditOnlyPaymentFromCustAccIcon'

interface CreditPaymentButtonProps {
  creditBalance: number
  selected: boolean
  onSelect: () => void
  disabled?: boolean
  isBalanceLoading?: boolean
  testId?: string
}

export const CreditPaymentButton: FC<CreditPaymentButtonProps> = ({
  creditBalance,
  selected,
  onSelect,
  disabled = false,
  isBalanceLoading = false,
  testId,
}) => {
  const { getTranslation } = useGetTranslations()
  const { formatMoney } = useAppConfig()
  const balanceId = useId()

  return (
    <div className="flex w-45 flex-col gap-3">
      <button
        type="button"
        data-testid={testId}
        onClick={onSelect}
        disabled={disabled}
        aria-describedby={balanceId}
        className={cn(
          'flex h-30 w-45 flex-col items-center justify-center gap-2.5 rounded-2xl border px-6 py-4',
          disabled
            ? 'border-neutral-medium-lighter bg-neutral-light cursor-not-allowed opacity-50'
            : selected
              ? 'border-success bg-success-very-light border-2'
              : 'border-neutral-medium-lighter hover:border-neutral-medium bg-white'
        )}
      >
        <CreditOnlyPaymentFromCustAccIcon width={20} height={20} />
        <span className="text-neutral-darker w-full px-4 text-center text-sm leading-tight">
          {getTranslation('confirmationPayWithCreditButtonText')}
        </span>
      </button>
      <p id={balanceId} className="text-neutral-dark text-sm">
        {getTranslation('confirmationPayWithCreditLimitButtonText')}{' '}
        {isBalanceLoading ? (
          <Spinner className="border-neutral-medium-lighter border-t-neutral-dark inline-block size-3 border-2 align-middle" />
        ) : (
          formatMoney(creditBalance)
        )}
      </p>
    </div>
  )
}
